import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Modal, IconButton } from '@mui/material';
import { X, ChevronRight, ChevronLeft, Download } from 'lucide-react';
import { logEvent } from '../../cloudwatchLogger';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';

const FilePreviewModal = ({ isOpen, onClose, fileData, chat }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState('asc');
  const [feedbackMessage, setFeedbackMessage] = useState("");

  if (!fileData || fileData.length === 0) return null;

  // Extract column headers from the first row of data and remove the last column
  const columns = Object.keys(fileData[0]).slice(0, -1);

  // Function to format column headers
  const formatHeader = (header) => {
    return header
      .replace(/_/g, ' ') // Replace underscores with spaces
      .split(' ') // Split into words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize first letter of each word
      .join(' '); // Join words with spaces
  };

  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const disablePreviousButton = currentPage === 1;
  const disableNextButton = currentPage * rowsPerPage >= fileData.length;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column) => {
    const sortedData = [...fileData].sort((a, b) => {
      const valueA = a[column]?.toLowerCase() || '';
      const valueB = b[column]?.toLowerCase() || '';
      return sortOrder === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    });
    fileData = sortedData;
    setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
  };

  const formatCurrency = (value, key) => {
    if (key === 'Total Donors') {
      return value;
    }
    if (typeof value === 'number') {
      return `$${value.toLocaleString('en-US')}`;
    }
    if (typeof value === 'string' && !isNaN(parseFloat(value))) {
      return `$${parseFloat(value).toLocaleString('en-US')}`;
    }
    return value;
  };

  const renderTableCell = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') {
      return value.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    return value;
  };

  const handleFeedback = (isPositive) => {
    const feedbackType = isPositive ? 'Positive' : 'Negative';
    logEvent({
      action: 'FeedbackQuery',
      details: `Query associated with ${feedbackType.toLowerCase()} feedback: ${chat.question}`,
      type: 'feedback'
    });

    // Set the feedback message
    setFeedbackMessage(`Thank you for your ${feedbackType.toLowerCase()} feedback!`);

    // Clear the feedback message after 3 seconds
    setTimeout(() => {
      setFeedbackMessage("");
    }, 3000);
  };

  const handleDownload = () => {
    const ws = utils.json_to_sheet(fileData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Sheet1");
    const wbout = write(wb, { bookType: 'xlsx', type: 'binary' });
    const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
    saveAs(blob, 'file_data.xlsx');
  };

  const s2ab = (s) => {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80%',
          maxWidth: '1200px',
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>File Preview (Limited to 1500 rows)</h2>
          <div>
            <IconButton onClick={handleDownload}>
              <Download size={20} />
            </IconButton>
            <IconButton onClick={onClose}>
              <X size={20} />
            </IconButton>
          </div>
        </div>
        <TableContainer component={Paper} style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((header, index) => (
                  <TableCell
                    key={index}
                    className="px-1.5 cursor-pointer"
                    onClick={() => handleSort(header)}
                    style={{
                      color: 'var(--foundation-primary-black-light-active, #8E8E8E)',
                      fontFamily: 'Inter',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: '400',
                      lineHeight: '120%'
                    }}
                  >
                    <div className="flex items-center min-h-[24px] group relative my-2.5">
                      <span className="truncate text-[#8E8E8E] font-inter text-[0.8125rem] leading-[120%]">
                        {formatHeader(header)} {/* Format the header */}
                      </span>
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fileData.slice(startIndex, endIndex).map((row, rowIndex) => (
                <TableRow key={rowIndex} className="hover:bg-gray-50 border-b border-[#F2F2F2]">
                  {columns.map((column, colIndex) => {
                    const cell = row[column];
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    const isEmail = typeof cell === 'string' && emailRegex.test(cell);
                    const isAmount = column.includes('Total') || column.includes('Amount') || column.includes('Contribution');

                    const displayValue = isAmount
                      ? formatCurrency(cell, column)
                      : isEmail
                        ? <a href={`mailto:${cell}`} className="text-[#40C5AF] font-inter text-[0.8125rem] font-normal leading-[120%]">{cell}</a>
                        : renderTableCell(cell);

                    return (
                      <TableCell key={colIndex} className="px-1.5 py-2">
                        <div
                          className={`text-[0.8125rem] font-inter my-2 ${
                            isEmail
                              ? 'inline-flex p-1 justify-center items-center gap-2.5 rounded-[0.4375rem] bg-[#ECF9F7]'
                              : isAmount
                                ? 'inline-flex p-1 justify-center items-center gap-2.5 font-bold rounded-[0.4375rem] bg-[#EBAE12] bg-opacity-20'
                                : ''
                          }`}
                          style={{
                            color: isAmount ? '#BC8B0E' : '#000',
                            fontWeight: 'normal'
                          }}
                        >
                          {displayValue}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className="flex w-full justify-end items-start" style={{ marginTop: '-12px' }}>
          <div className="flex items-end gap-4 relative z-10">
            <div className="flex w-[12.4375rem] items-center w-[295px] px-2 justify-between border border-[#D9D9D9] rounded-[7px] bg-white shadow-sm">
              <div className="flex items-center gap-2">
                <span className="text-[#000] font-inter text-[0.8125rem] font-normal leading-[120%]">
                  {startIndex + 1} - {Math.min(endIndex, fileData.length)} of {fileData.length} items
                </span>
                <div className="flex gap-1">
                  <button
                    className="flex justify-center items-center px-2 py-1 gap-2.5
                      text-gray-600 hover:bg-gray-50 disabled:opacity-50 
                      disabled:cursor-not-allowed w-8 h-8"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={disablePreviousButton}
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    className="flex justify-center items-center px-2 py-1 gap-2.5
                      text-gray-600 hover:bg-gray-50 disabled:opacity-50 
                      disabled:cursor-not-allowed w-8 h-8"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={disableNextButton}
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FilePreviewModal;